import { channelFromBackend } from '@/lib/channels';
import { isSparseApplicationListRow } from '@/lib/companyApplicationsList';
import type { Application, ApplicationState, SocialProfileSubmission, SocialProfileType } from '@/types';

type FirestoreTimestampLike =
  | { _seconds: number; _nanoseconds?: number }
  | { seconds: number; nanoseconds?: number }
  | string;

export type FirestoreApplicationDocData = Record<string, unknown>;

export type ParsedApplicationPath = {
  companyId: string;
  positionId: string;
  applicationId: string;
};

const APPLICATION_PATH_RE =
  /^companies\/([^/]+)\/jobPositions\/([^/]+)\/jobApplications\/([^/]+)$/;

export function parseApplicationDocPath(path: string): ParsedApplicationPath | null {
  const match = path.match(APPLICATION_PATH_RE);
  if (!match) return null;
  return {
    companyId: match[1]!,
    positionId: match[2]!,
    applicationId: match[3]!,
  };
}

export function mapFirestoreTimestamp(value: unknown): string {
  if (!value) return new Date().toISOString();
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null) {
    const ts = value as FirestoreTimestampLike;
    const seconds = '_seconds' in ts ? ts._seconds : 'seconds' in ts ? ts.seconds : undefined;
    if (typeof seconds === 'number') {
      const nanos =
        '_nanoseconds' in ts ? ts._nanoseconds
        : 'nanoseconds' in ts ? ts.nanoseconds
        : 0;
      return new Date(seconds * 1000 + Math.floor((nanos ?? 0) / 1e6)).toISOString();
    }
  }
  return new Date().toISOString();
}

function readCollectedScore(collected: Record<string, unknown>): number | undefined {
  const entry = collected['screening_score'];
  if (!entry || typeof entry !== 'object') return undefined;
  const value = (entry as { value?: unknown }).value;
  return typeof value === 'number' ? value : undefined;
}

function readIdentityMismatch(
  collected: Record<string, unknown>,
): Application['identityMismatch'] | undefined {
  const entry = collected['form_resume_identity_mismatch'];
  if (!entry || typeof entry !== 'object') return undefined;
  const value = (entry as { value?: unknown }).value;
  if (
    value
    && typeof value === 'object'
    && (value as { hasMismatch?: boolean }).hasMismatch === true
  ) {
    return value as Application['identityMismatch'];
  }
  return undefined;
}

function mapCandidateSocialProfiles(snapshot: Record<string, unknown>): SocialProfileSubmission[] | undefined {
  const profiles = snapshot['socialProfiles'];
  if (!Array.isArray(profiles) || profiles.length === 0) return undefined;
  const mapped = profiles
    .filter((entry) => entry && typeof entry === 'object' && typeof (entry as { url?: unknown }).url === 'string')
    .map((entry) => {
      const row = entry as { type: SocialProfileType; label?: string; customLabel?: string; url: string };
      return {
        type: row.type,
        label: row.label ?? row.customLabel,
        url: String(row.url).trim(),
      };
    })
    .filter((entry) => entry.url);
  return mapped.length > 0 ? mapped : undefined;
}

/**
 * Maps a Firestore `jobApplications` document to a list-dense Application row.
 * Omits positionTitle when unknown — cache merge fills it via `mergeApplicationDisplayFields`.
 */
export function mapFirestoreDocToListRow(
  data: FirestoreApplicationDocData,
  path: ParsedApplicationPath,
  options?: { positionTitle?: string },
): Application | null {
  const snapshot = (data['candidateSnapshot'] as Record<string, unknown> | undefined) ?? {};
  const firstName = String(snapshot['firstName'] ?? '').trim();
  const lastName = String(snapshot['lastName'] ?? '').trim();
  const email = String(snapshot['email'] ?? '').trim();
  const collected = (data['collectedData'] as Record<string, unknown> | undefined) ?? {};

  const row: Application = {
    id: path.applicationId,
    companyId: path.companyId,
    positionId: path.positionId,
    positionTitle: options?.positionTitle ?? '',
    postingId: String(data['jobPostingId'] ?? ''),
    channel: channelFromBackend(String(data['channel'] ?? 'other')),
    candidateFirstName: firstName,
    candidateLastName: lastName,
    candidateEmail: email,
    candidatePhone: snapshot['phone'] ? String(snapshot['phone']) : undefined,
    candidateLinkedinUrl: snapshot['linkedinUrl'] ? String(snapshot['linkedinUrl']) : undefined,
    candidateSocialProfiles: mapCandidateSocialProfiles(snapshot),
    state: data['state'] as ApplicationState,
    aiScore: readCollectedScore(collected),
    screeningReports: [],
    interviews: [],
    history: [],
    appliedAt: mapFirestoreTimestamp(data['appliedAt']),
    updatedAt: mapFirestoreTimestamp(data['updatedAt']),
    hasIdentityMismatch: readIdentityMismatch(collected) !== undefined,
    identityMismatch: readIdentityMismatch(collected),
    hasScheduledInterview: data['hasScheduledInterview'] === true ? true : undefined,
    lastFailureReason: data['lastFailureReason'] as Application['lastFailureReason'],
    archived: data['archived'] === true ? true : data['archived'] === false ? false : undefined,
    archivedAt: data['archivedAt'] ? mapFirestoreTimestamp(data['archivedAt']) : undefined,
    archivedBy: data['archivedBy'] ? String(data['archivedBy']) : undefined,
  };

  if (isSparseApplicationListRow(row)) return null;
  return row;
}
